import {
  customAlertApiRef,
  WbBadge,
  WbButton,
  WbCard,
  WbCardContent,
  WbInfoTooltip,
  useEnabledFeatures,
} from '@agilelab/plugin-wb-platform';
import {
  WittyUserOAuthConnection,
  wittyMcpConnectionsApiRef,
} from '@agilelab/plugin-wb-witty-react';
import { ResponseErrorPanel } from '@mainstage/core-components';
import { useApi } from '@mainstage/core-plugin-api';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useEffect, useRef, useState } from 'react';

const useStyles = makeStyles(
  (theme: Theme) => ({
    connectionRow: {
      gap: theme.spacing(2),
      flexWrap: 'wrap',
    },
    connectionName: {
      flex: '1 1 240px',
      minWidth: 0,
    },
    connectionIdentity: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) 120px',
      alignItems: 'center',
      gap: theme.spacing(1),
      width: '100%',
      [theme.breakpoints.down('xs')]: {
        gridTemplateColumns: 'minmax(0, 1fr)',
      },
    },
    connectionActions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flex: '0 0 104px',
      marginLeft: 'auto',
    },
    targetedConnection: {
      backgroundColor: theme.palette.action.selected,
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  }),
  { name: 'UserMcpOAuthSettings' },
);

function UserMcpOAuthSettingsContent() {
  const classes = useStyles();
  const api = useApi(wittyMcpConnectionsApiRef);
  const alertApi = useApi(customAlertApiRef);
  const [connections, setConnections] = useState<WittyUserOAuthConnection[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState<string>();
  const [error, setError] = useState<Error>();
  const mcpConnectionsCardRef = useRef<HTMLDivElement>(null);
  const selectedConnectionButtonRef = useRef<HTMLButtonElement>(null);
  const selectedConnectionId = new URLSearchParams(window.location.search).get(
    'mcp_connection',
  );

  useEffect(() => {
    if (
      loading ||
      !selectedConnectionId ||
      !connections.some(connection => connection.id === selectedConnectionId)
    ) {
      return undefined;
    }

    let scrollFrame: number;
    const layoutFrame = requestAnimationFrame(() => {
      scrollFrame = requestAnimationFrame(() => {
        mcpConnectionsCardRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        selectedConnectionButtonRef.current?.focus({ preventScroll: true });
      });
    });

    return () => {
      cancelAnimationFrame(layoutFrame);
      cancelAnimationFrame(scrollFrame);
    };
  }, [connections, loading, selectedConnectionId]);

  useEffect(() => {
    const oauthResult = new URLSearchParams(window.location.search).get(
      'mcp_oauth',
    );
    if (oauthResult) {
      alertApi.post({
        message:
          oauthResult === 'connected'
            ? 'MCP connection authorized'
            : 'MCP authorization failed',
        severity: oauthResult === 'connected' ? 'success' : 'error',
      });
      const url = new URL(window.location.href);
      url.searchParams.delete('mcp_oauth');
      window.history.replaceState({}, '', url);
    }
  }, [alertApi, api]);

  useEffect(() => {
    let active = true;
    api
      .listUserOAuthConnections()
      .then(result => {
        if (active) setConnections(result);
      })
      .catch(cause => {
        if (active)
          setError(cause instanceof Error ? cause : new Error(String(cause)));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api]);

  const handleConnection = async (connection: WittyUserOAuthConnection) => {
    setPendingId(connection.id);
    try {
      if (connection.connected) {
        await api.disconnectOAuth(connection.id);
        setConnections(current =>
          current.map(item =>
            item.id === connection.id ? { ...item, connected: false } : item,
          ),
        );
        alertApi.post({
          message: `${connection.display_name} disconnected`,
          severity: 'success',
        });
      } else {
        const authorizationUrl = await api.connectOAuth(connection.id);
        window.location.assign(authorizationUrl);
      }
    } catch (cause) {
      if (!connection.connected) {
        alertApi.post({
          message: `Unable to authorize ${connection.display_name}. Please contact an administrator to verify that the MCP connection is configured correctly.`,
          severity: 'error',
        });
      } else {
        alertApi.post({ error: cause, severity: 'error' });
      }
      setPendingId(undefined);
    }
  };

  if (!loading && !error && connections.length === 0) return null;

  return (
    <Grid container spacing={3}>
      <Grid ref={mcpConnectionsCardRef} item xs={12}>
        <WbCard
          title="MCP Connections"
          icon={
            <WbInfoTooltip title="Connect your personal account to external services used by Witty. Your connections are private to you and are not shared with other users." />
          }
        >
          <WbCardContent>
            {loading && <LinearProgress />}
            {error && <ResponseErrorPanel error={error} />}
            {!loading && !error && connections.length > 0 && (
              <List disablePadding>
                {connections.map(connection => (
                  <ListItem
                    key={connection.id}
                    divider
                    className={
                      selectedConnectionId === connection.id
                        ? `${classes.connectionRow} ${classes.targetedConnection}`
                        : classes.connectionRow
                    }
                    aria-current={
                      selectedConnectionId === connection.id
                        ? 'true'
                        : undefined
                    }
                  >
                    <ListItemText
                      primary={
                        <Box className={classes.connectionIdentity}>
                          <Typography>{connection.display_name}</Typography>
                          {connection.connected ? (
                            <WbBadge variant="stroke" color="success">
                              Connected
                            </WbBadge>
                          ) : (
                            <WbBadge variant="disabled">Not connected</WbBadge>
                          )}
                        </Box>
                      }
                      className={classes.connectionName}
                    />
                    <Box
                      className={classes.connectionActions}
                      role="group"
                      aria-label={`${connection.display_name} connection actions`}
                    >
                      <WbButton
                        ref={
                          selectedConnectionId === connection.id
                            ? selectedConnectionButtonRef
                            : undefined
                        }
                        variant={connection.connected ? 'text' : 'outlined'}
                        color={connection.connected ? 'secondary' : 'primary'}
                        label={connection.connected ? 'Disconnect' : 'Connect'}
                        fullWidth
                        disabled={pendingId === connection.id}
                        onClick={() => handleConnection(connection)}
                      />
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </WbCardContent>
        </WbCard>
      </Grid>
    </Grid>
  );
}

export function UserMcpOAuthSettings() {
  const { data: features, loading } = useEnabledFeatures();

  if (loading || !features.wittyAssistantEnabled) return null;

  return <UserMcpOAuthSettingsContent />;
}
